import { z } from 'zod'
import { addMonths, endOfMonth, todayIso } from '../../lib/dates'

/**
 * Books health (ACCOUNTING_ASSISTANT.md §4) keeps its month and "Show dismissed" in the URL: shareable,
 * back-button safe. An invalid value falls back to the default (the current month, dismissed hidden).
 */
export const booksHealthSearchSchema = z.object({
  month: z
    .string()
    .regex(/^\d{4}-(0[1-9]|1[0-2])$/)
    .optional()
    .catch(undefined),
  dismissed: z.boolean().optional().catch(undefined),
})
export type BooksHealthSearch = z.infer<typeof booksHealthSearchSchema>

/** How many months back the month picker reaches. */
export const BOOKS_HEALTH_MONTHS = 12

/** `YYYY-MM` of a date (`YYYY-MM-DD`). */
export const monthOf = (iso: string): string => iso.slice(0, 7)

/** The current month in Muscat. */
export const currentMonth = (today: string = todayIso()): string => monthOf(today)

/**
 * The period a month is checked over: from its first day to its last, or to today for the current month
 * (nothing after today is in the books yet). A month after the current one reads as the current one.
 */
export function monthPeriod(month: string, today: string = todayIso()): { from: string; to: string } {
  const current = currentMonth(today)
  const m = month > current ? current : month
  const from = `${m}-01`
  const end = endOfMonth(from)
  return { from, to: end > today ? today : end }
}

/** The month the URL asks for, or the current one (a future month falls back to it too). */
export function resolveMonth(month: string | undefined, today: string = todayIso()): string {
  const current = currentMonth(today)
  return month && month <= current ? month : current
}

/** The months the picker offers, newest first: the current one and `BOOKS_HEALTH_MONTHS - 1` before it. */
export function monthChoices(selected: string, today: string = todayIso()): string[] {
  const start = `${currentMonth(today)}-01`
  const list = Array.from({ length: BOOKS_HEALTH_MONTHS }, (_, i) => monthOf(addMonths(start, -i)))
  return list.includes(selected) ? list : [...list, selected].sort().reverse()
}

/** The month before or after `month`. */
export const shiftMonth = (month: string, by: number): string => monthOf(addMonths(`${month}-01`, by))
